import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';

const EMPTY_FORM = {
    teamId: '',
    title: '',
    description: '',
    responsibleUserIds: [],
    totalWeight: 1000
};

const getId = (value) => value?._id || value || '';

const buildInitialForm = (objective, defaultTotalWeight, initialTeamId) => {
    if (!objective) {
        return {
            ...EMPTY_FORM,
            teamId: initialTeamId || '',
            totalWeight: defaultTotalWeight || EMPTY_FORM.totalWeight
        };
    }

    return {
        teamId: getId(objective.teamId),
        title: objective.title || '',
        description: objective.description || '',
        responsibleUserIds: Array.isArray(objective.responsibleUserIds)
            ? objective.responsibleUserIds.map(getId).filter(Boolean)
            : [],
        totalWeight: Number(objective.totalWeight || defaultTotalWeight || EMPTY_FORM.totalWeight)
    };
};

const OkrObjectiveDialog = ({ visible, objective, teams, users, defaultTotalWeight, initialTeamId, saving, onHide, onSubmit }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const isEditMode = Boolean(objective?._id);
    const isTeamLocked = Boolean(initialTeamId) || isEditMode;

    useEffect(() => {
        if (visible) {
            setForm(buildInitialForm(objective, defaultTotalWeight, initialTeamId));
            setErrors({});
        }
    }, [defaultTotalWeight, initialTeamId, objective, visible]);

    const title = useMemo(() => (isEditMode ? 'Editar objetivo principal' : 'Agregar objetivo principal'), [isEditMode]);

    const teamOptions = useMemo(
        () =>
            (teams || []).map((team) => ({
                label: team.name || 'Equipo sin nombre',
                value: team._id
            })),
        [teams]
    );

    const userOptions = useMemo(
        () =>
            (users || []).map((user) => ({
                label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Usuario sin nombre',
                value: user._id
            })),
        [users]
    );

    const updateField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!form.teamId) nextErrors.teamId = 'Selecciona un equipo.';
        if (!form.title?.trim()) nextErrors.title = 'El título del objetivo es obligatorio.';
        if (!form.totalWeight || Number(form.totalWeight) <= 0) nextErrors.totalWeight = 'El peso total debe ser mayor que 0.';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        onSubmit({
            teamId: form.teamId,
            title: form.title.trim(),
            description: form.description || '',
            responsibleUserIds: form.responsibleUserIds || [],
            totalWeight: Number(form.totalWeight)
        });
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={saving} />
            <Button label={isEditMode ? 'Guardar cambios' : 'Agregar objetivo'} icon="pi pi-check" onClick={handleSubmit} loading={saving} />
        </div>
    );

    return (
        <Dialog header={title} visible={visible} onHide={onHide} footer={footer} className="w-full md:w-34rem" modal draggable={false}>
            <div className="surface-50 border-round p-3 mb-3">
                <div className="font-semibold mb-1">Objetivo principal</div>
                <small className="text-color-secondary">
                    Define qué se quiere lograr. No lleva puntos directamente.
                </small>
            </div>

            <div className="formgrid grid">
                <div className="field col-12">
                    <label htmlFor="okr-objective-team" className="font-medium">Equipo</label>
                    <Dropdown
                        id="okr-objective-team"
                        value={form.teamId}
                        options={teamOptions}
                        onChange={(event) => updateField('teamId', event.value)}
                        filter
                        disabled={isTeamLocked}
                        className={`w-full mt-2 ${errors.teamId ? 'p-invalid' : ''}`}
                        placeholder="Selecciona un equipo"
                    />
                    {errors.teamId ? <small className="p-error">{errors.teamId}</small> : null}
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-objective-title" className="font-medium">Título del objetivo principal</label>
                    <InputText
                        id="okr-objective-title"
                        value={form.title}
                        onChange={(event) => updateField('title', event.target.value)}
                        className={`w-full mt-2 ${errors.title ? 'p-invalid' : ''}`}
                    />
                    {errors.title ? <small className="p-error">{errors.title}</small> : null}
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-objective-description" className="font-medium">Descripción</label>
                    <InputTextarea
                        id="okr-objective-description"
                        value={form.description}
                        onChange={(event) => updateField('description', event.target.value)}
                        rows={3}
                        className="w-full mt-2"
                    />
                </div>

                <div className="field col-12 md:col-8">
                    <label htmlFor="okr-objective-responsibles" className="font-medium">Responsables</label>
                    <MultiSelect
                        id="okr-objective-responsibles"
                        value={form.responsibleUserIds}
                        options={userOptions}
                        onChange={(event) => updateField('responsibleUserIds', event.value)}
                        filter
                        display="chip"
                        className="w-full mt-2"
                        placeholder="Selecciona responsables"
                    />
                </div>

                <div className="field col-12 md:col-4">
                    <label htmlFor="okr-objective-weight" className="font-medium">Peso de referencia</label>
                    <InputNumber
                        id="okr-objective-weight"
                        value={form.totalWeight}
                        onValueChange={(event) => updateField('totalWeight', event.value)}
                        min={1}
                        useGrouping
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        className={`w-full mt-2 ${errors.totalWeight ? 'p-invalid' : ''}`}
                    />
                    <small className="text-color-secondary">Referencia para distribuir resultados clave medibles.</small>
                    {errors.totalWeight ? <small className="p-error">{errors.totalWeight}</small> : null}
                </div>
            </div>
        </Dialog>
    );
};

export default OkrObjectiveDialog;
